// cSpell:ignore Eindafrekening vertrek
// From when a departure settlement ("Eindafrekening bij vertrek") settles
// its lease, as the wizard proposes it (D8, Lewis 2026-10-16, "like the
// market"):
// - the day after the last confirmed settlement in Rentio that covered the
//   lease (a departure, or a regular or building settlement with a row for
//   it);
// - the lease's start when Rentio never settled it;
// - never more than five years before the departure: charges older than
//   that can no longer be regularized in Belgium.
// The broker sees the start in the wizard's first step and can change it.
// The period runs up to the lease's last day, as before.

import { Interval, SliceIntervals } from "./engine";
import { ProvisionSlice } from "./provisionHistory";
import { leaseAccountIds } from "./settledProvisions.utils";
import { ContractRef, Invoice, Settlement } from "./types";

// How far back charges can be regularized (the Belgian five-year limit).
export const REGULARIZATION_LIMIT_YEARS = 5;

const SLICES: ProvisionSlice[] = ["common", "private"];

const isoDay = (date: Date): string => date.toISOString().slice(0, 10);

const utcDay = (iso: string): Date => {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const dayAfter = (iso: string): string => {
  const date = utcDay(iso);
  date.setUTCDate(date.getUTCDate() + 1);
  return isoDay(date);
};

// The first day of the five years that end on `lastDay`, both included:
// 31/10/2026 → 01/11/2021. A 29 February that does not exist five years
// earlier rolls over to 1 March, which is still exactly five years.
export const regularizationLimitStart = (lastDay: string): string => {
  const next = utcDay(dayAfter(lastDay));
  return isoDay(
    new Date(
      Date.UTC(
        next.getUTCFullYear() - REGULARIZATION_LIMIT_YEARS,
        next.getUTCMonth(),
        next.getUTCDate(),
      ),
    ),
  );
};

const lastDayOf = (intervals: Interval[] | undefined): string | undefined =>
  (intervals ?? [])
    .map(interval => interval.to.slice(0, 10))
    .sort()
    .pop();

// The lease's monthly provision of one slice, as the lease reads today. A
// lease without the split carries its whole provision as the common slice
// (see contractProvisionSlices).
const monthlyProvisionOf = (
  lease: Pick<
    ContractRef,
    "monthlyProvision" | "monthlyProvisionCommon" | "monthlyProvisionPrivate"
  >,
  slice: ProvisionSlice,
): number => {
  if (
    lease.monthlyProvisionCommon == null &&
    lease.monthlyProvisionPrivate == null
  ) {
    return slice === "common" ? lease.monthlyProvision ?? 0 : 0;
  }
  return (
    (slice === "common"
      ? lease.monthlyProvisionCommon
      : lease.monthlyProvisionPrivate) ?? 0
  );
};

// The last day of the lease that confirmed settlements in Rentio already
// settled; undefined when Rentio never settled it.
//
// A lease with a provision reads the provision ledger (settledProvisionsInTree:
// per slice, the days earlier settlements counted, an earlier lease of the
// same people in the same unit included). Each slice it carries a provision
// on, or that a settlement counted, is settled up to its own last day, and
// the lease up to the earliest of those: a building settlement of 2025 that
// counted the common slice and an interim settlement of the unit that
// counted the private slice up to 30/06/2026 leave the common months of 2026
// open, so the lease is settled up to 31/12/2025. A slice no settlement ever
// counted leaves the whole lease open. Starting earlier never bills twice:
// a departure skips the months and costs already settled anyway.
//
// A lease without any provision has nothing in that ledger: it reads the
// periods of the confirmed settlements that hold a row for it (or for an
// earlier lease of the same people in the same unit).
export const lastSettledDayOfLease = (params: {
  lease: Pick<
    ContractRef,
    | "id"
    | "unitId"
    | "payerCandidates"
    | "tenantAccountId"
    | "monthlyProvision"
    | "monthlyProvisionCommon"
    | "monthlyProvisionPrivate"
  >;
  // The lease's days per slice earlier settlements counted (see
  // ISettledProvisionsByLease.byContract).
  settledBySlice?: SliceIntervals;
  // The settlements of the lease's unit and of its building.
  settlements: Settlement[];
  // The settlement being made, never counted.
  ownId?: string;
}): string | undefined => {
  const { lease, settledBySlice, settlements, ownId } = params;
  const slices = SLICES.filter(
    slice =>
      monthlyProvisionOf(lease, slice) > 0 ||
      (settledBySlice?.[slice]?.length ?? 0) > 0,
  );
  if (slices.length > 0) {
    const ends = slices.map(slice => lastDayOf(settledBySlice?.[slice]));
    if (ends.some(end => end == null)) return undefined;
    return (ends as string[]).sort()[0];
  }
  const people = leaseAccountIds(lease);
  return settlements
    .filter(
      settlement =>
        settlement.status === "confirmed" &&
        settlement.id !== ownId &&
        (settlement.snapshot?.perContract ?? []).some(
          row =>
            row.contractId === lease.id ||
            (String(row.unitId) === String(lease.unitId) &&
              (row.leaseAccountIds ?? []).some(id => people.includes(id))),
        ),
    )
    .map(settlement => settlement.periodTo.slice(0, 10))
    .sort()
    .pop();
};

// Why the proposed start is what it is:
// - "afterLastSettlement": the day after what Rentio last settled
// - "leaseStart": Rentio never settled the lease (or only an earlier lease
//   of the same people, which ended before this one started)
// - "settledThroughDeparture": Rentio settled the lease up to its last day
//   or beyond already. The proposal is then the lease's last day alone (Y05,
//   2026-10-19): starting at the lease's start reopened every year before
//   the first settlement (€ 840 of 2025 provisions refunded without a
//   cost). The wizard says the lease is settled and allows a final
//   settlement of € 0 (Y06).
export type TDepartureStartBasis =
  | "afterLastSettlement"
  | "leaseStart"
  | "settledThroughDeparture";

export interface IDepartureStart {
  from: string;
  basis: TDepartureStartBasis;
  // The five-year limit moved the start later.
  capped: boolean;
}

// The day the five-year limit runs back from (M25): the departure, or today
// when the lease still runs past it. Charges are regularized now, so a lease
// that ends in 2035 still reaches back five years from today, never from its
// end.
export const regularizationLimitAnchor = (params: {
  departureDay: string;
  today?: string;
}): string => {
  const departureDay = params.departureDay.slice(0, 10);
  return params.today && params.today < departureDay
    ? params.today
    : departureDay;
};

export const departureDefaultStart = (params: {
  leaseStart: string;
  // The lease's last day, where the departure settlement ends.
  departureDay: string;
  // See lastSettledDayOfLease.
  lastSettledDay?: string;
  // Today's calendar day (see regularizationLimitAnchor); without it the
  // limit runs back from the departure.
  today?: string;
}): IDepartureStart => {
  const leaseStart = params.leaseStart.slice(0, 10);
  const departureDay = params.departureDay.slice(0, 10);
  const afterLast = params.lastSettledDay
    ? dayAfter(params.lastSettledDay)
    : undefined;
  let from = leaseStart;
  let basis: TDepartureStartBasis = "leaseStart";
  if (afterLast && afterLast > departureDay) {
    from = departureDay;
    basis = "settledThroughDeparture";
  } else if (afterLast && afterLast > leaseStart) {
    from = afterLast;
    basis = "afterLastSettlement";
  }
  const limitStart = regularizationLimitStart(
    regularizationLimitAnchor({ departureDay, today: params.today }),
  );
  const capped = from < limitStart;
  return { from: capped ? limitStart : from, basis, capped };
};

// Where a departure of a lease Rentio settled up to `until` starts when the
// five-year limit moves it later than the day after (M25): the period step
// then names that day instead of "the day after". Undefined otherwise.
export const settledUntilCappedStart = (params: {
  until: string;
  departureDay: string;
  today?: string;
}): string | undefined => {
  const limitStart = regularizationLimitStart(
    regularizationLimitAnchor({
      departureDay: params.departureDay,
      today: params.today,
    }),
  );
  return dayAfter(params.until) < limitStart ? limitStart : undefined;
};

// The first day of the earliest cost Rentio has for the property (a unit's
// own costs and its building's); undefined without costs.
export const firstCostStart = (
  costs: Pick<Invoice, "periodFrom">[],
): string | undefined =>
  costs
    .map(cost => cost.periodFrom?.slice(0, 10))
    .filter((day): day is string => !!day)
    .sort()[0];

// The first cost's start when the period starts before it: those months
// were probably settled outside Rentio (a lease entered in Rentio later), so
// the wizard asks the broker to check the start. Undefined otherwise.
export const startsBeforeFirstCost = (params: {
  from: string;
  costs: Pick<Invoice, "periodFrom">[];
}): string | undefined => {
  const first = firstCostStart(params.costs);
  return first && params.from && params.from.slice(0, 10) < first
    ? first
    : undefined;
};

// The first cost's start when the whole period, up to `to`, lies before it
// (M47): no cost of that period is in Rentio yet, which no start date fixes.
// Undefined otherwise.
export const endsBeforeFirstCost = (params: {
  to: string;
  costs: Pick<Invoice, "periodFrom">[];
}): string | undefined => {
  const first = firstCostStart(params.costs);
  return first && params.to && params.to.slice(0, 10) < first
    ? first
    : undefined;
};

// Up to when Rentio already settled a leaving lease, and in which confirmed
// settlements (findings Y05 and Y06 of 19/10/2026): what the departure's period
// step says next to its start, with a link to each. Undefined when Rentio
// never settled the lease.
export interface ILeaseSettledThrough {
  // The lease's last day Rentio settled (see lastSettledDayOfLease).
  until: string;
  // Settled up to the lease's last day or beyond: nothing is left to settle
  // but a cost that came in since, so a final settlement of € 0 is allowed.
  isFull: boolean;
  // Settled beyond the lease's last day (its end moved earlier after that
  // settlement, M07): the next settlement of the unit or the building
  // corrects the days after it (decision 3), which the wizard says.
  pastEnd?: string;
  // The settlements that reach that day, the most recent first; the property
  // they are on opens them.
  settlements: { id: string; name: string; propertyId?: string }[];
}

export const leaseSettledThrough = (params: {
  lease: Pick<
    ContractRef,
    "id" | "unitId" | "payerCandidates" | "tenantAccountId" | "endDate"
  >;
  lastSettledDay?: string;
  settlements: Settlement[];
  ownId?: string;
  nameOf: (settlement: Settlement) => string;
}): ILeaseSettledThrough | undefined => {
  const { lease, lastSettledDay, settlements, ownId, nameOf } = params;
  if (!lastSettledDay) return undefined;
  const until = lastSettledDay.slice(0, 10);
  const people = leaseAccountIds(lease);
  const holding = settlements.filter(
    settlement =>
      settlement.status === "confirmed" &&
      settlement.id !== ownId &&
      settlement.periodFrom.slice(0, 10) <= until &&
      (settlement.snapshot?.perContract ?? []).some(
        row =>
          row.contractId === lease.id ||
          (String(row.unitId) === String(lease.unitId) &&
            (row.leaseAccountIds ?? []).some(id => people.includes(id))),
      ),
  );
  const reaching = holding.filter(
    settlement => settlement.periodTo.slice(0, 10) >= until,
  );
  const shown = (reaching.length > 0 ? reaching : holding).sort((a, b) =>
    b.periodTo.localeCompare(a.periodTo),
  );
  const leaseEnd = lease.endDate.slice(0, 10);
  // Past the end only when a settlement settled the lease up to a later end
  // it froze: a year settlement of a lease that ended inside it settled
  // only the lease's days.
  const pastEnd =
    until > leaseEnd &&
    holding.some(settlement => {
      const row = settlement.snapshot?.perContract.find(
        candidate => candidate.contractId === lease.id,
      );
      const frozen =
        row?.leaseEnd ??
        settlement.snapshot?.statements?.[`tenant:${lease.id}`]?.contractEnd;
      return !!frozen && frozen.slice(0, 10) > leaseEnd;
    });
  return {
    until,
    isFull: until >= leaseEnd,
    ...(pastEnd ? { pastEnd: leaseEnd } : {}),
    settlements: shown.map(settlement => ({
      id: settlement.id,
      name: nameOf(settlement),
      propertyId: settlement.groupId ?? settlement.unitId,
    })),
  };
};
